(function () {
  /**
   * BatchGeoPrintTable - This module manages the actual print table
   * itself,which is a coordination between the row workers that put rows on
   * the DOM, the progressbar that shows how far along you are printing and
   * the container where it all ends up which is in the same physical spot as
   * the BatchGeoTableMap.
   *
   * @param {BatchGeoTableMap} tableMap This is a reference to the instance of
   * the BatchGeoTableMap. this is needed to get some of the data necessary
   * for the print table, and also to hide and show the tableMap itself while
   * showing print-view.
   *
   * @returns {Object} this exposes the activate, deactivate and
   * cancelPrinting methods to other modules.
   */
  function BatchGeoPrintTable(tableMap) {
    var CHUNK_SIZE_SCALE_UNIT = 128;

    // We render only a certain amount of 'chunks' of rows above the fold,
    // and this is to improve speed. and then append all the rest at the
    // very end.
    var ABOVE_THE_FOLD_ITERATION_AMOUNT = 4;
    var FORCE_LANDSCAPE = "force-landscape";

    var $printview = tableMap._cacheElements().printView;

    var printerProgressBar = new window.PrinterProgressBar();
    var templateStrategy = new window.PrintTableTemplates();

    // These are really private to both the work and the activate function,
    // but need to be in a higher scope so both worker and activate() can
    // access them. they are reset at deactivate()
    var rowsRemaining;
    var aboveTheFoldRenderIterationCount = ABOVE_THE_FOLD_ITERATION_AMOUNT;
    var laterRowsCache = "";
    var rows = null;
    var cols = null;
    var rowsLength = 0;
    var chunkSize = 0;

    // This flag (when true) halts the worker/recursive function.
    var cancelPrint = false;

    // These are cached jQuery element used while the progress bar is
    // running. and things are appended to the DOM. they are cleared at
    // deactivate().
    var $printTable;
    var $tableBody;
    var $progressBar;

    var self = this;

    function scaleChunkSize(totalRowAmount, chunkScaleUnit) {
      if (totalRowAmount < chunkScaleUnit) {
        return Math.floor(chunkScaleUnit / 8);
      }
      if (totalRowAmount < Math.floor(chunkScaleUnit * 8)) {
        return Math.floor(chunkScaleUnit / 4);
      }
      return chunkScaleUnit;
    }

    /**
     * createDataPrintModel - This is an impure function whose job is to
     * override the private values used inside of this module by making
     * copies to prevent side effects elsewhere. These items are the data
     * models the 'activate' function operates upon.
     *
     * @returns {none}  no return type, but see description
     */
    function createDataPrintModel(options) {
      rows = options.hideUnselectedTableRows
        ? tableMap.getSelectedDisplayData()
        : tableMap.batchGeoTable.getDisplayData();
      cols = tableMap.batchGeoTable.data.columns.slice();
      rowsLength = rows.length;
      chunkSize = scaleChunkSize(rowsLength, CHUNK_SIZE_SCALE_UNIT);
      rowsRemaining = rows.slice();
    }

    function determineChunkSize(chunkSize, rows) {
      if (rows.length < chunkSize) {
        return rows.length;
      }
      return chunkSize;
    }

    function cancelPrinting() {
      cancelPrint = true;
    }

    /**
     * activate - This is a recursive function, that continually is called
     * at the completion of a worker as long as there are still rows to
     * process. You can cancel it by flipping 'cancelPrint' to false via the
     * cancelPrint function. This will also bring up a progress bar as a
     * side effect, in addition to the webworker that builds the rows.
     *
     * At the very end, all built rows are appended to the DOM, they they
     * did not appear "above the fold." This presents the illusion of rows
     * being added immediately, but allows for the optimization of touching
     * the DOM as little as possible.
     *
     * @returns {boolean}  no return type, but see description
     */
    function activate(options) {
      options = Object.assign(
        {},
        {
          onComplete: $.noop,
        },
        options,
      );

      if (cancelPrint) {
        self.deactivate();
        window.batchGeoPrinter.printViewOff();
        options.onComplete.call(this);
        $progressBar = printerProgressBar.destroy();
        return false;
      }

      if (!rows || !rowsRemaining) {
        createDataPrintModel(options);
      }

      var rowWorker = new Worker(templateStrategy[options.tabletype].rowWorker);
      tableMap.batchGeoTable.hide();

      if (!$progressBar) {
        $progressBar = printerProgressBar.open(rows, cancelPrinting);
      }

      // TODO: Don't do all this table rendering stuff if shouldRenderTable is
      // disabled. Doing all this slows down time to print if it's not needed
      if (!$printTable) {
        const tableHtml = templateStrategy[options.tabletype].body(cols);
        $printview.html(tableHtml);
        $printTable = $printview.find(".batchgeo-print-table").eq(0);
      }

      // <chunk ..  |divider|  ..rowsRemaining  >
      //division point
      var divider = determineChunkSize(chunkSize, rows);

      //process this.
      var chunk = rowsRemaining
        .slice(0, divider)
        .map(function addColsToChunk(obj) {
          obj.cols = cols.slice();
          return obj;
        });

      //rows remaining.
      rowsRemaining = rowsRemaining.slice(divider, rowsRemaining.length);

      function workerOnError(error) {
        console.error(
          "Could not build rows as an error occurred in the row worker: " +
            error.message,
        );
        throw error.message;
      }

      function workerOnMessage(message) {
        var map = window.batchGeoGoogleMapsManager.map;
        printerProgressBar.updateProgress(rowsRemaining);

        //cache selector
        if (!$tableBody) {
          $tableBody = $printTable.find(".batchgeo-print-table-body").eq(0);
        }

        // render items above the fold but build the rest in memory,
        // touch the DOM one time for speed.
        if (aboveTheFoldRenderIterationCount > 0) {
          $tableBody.append(message.data);
          aboveTheFoldRenderIterationCount--;
        } else {
          laterRowsCache += message.data;
        }

        //base case.
        if (rowsRemaining.length > 0) {
          return activate(options);
        }

        $("body,#topBar").addClass(FORCE_LANDSCAPE);

        BatchGeoStore.dispatch({
          type: "PRINTER_MAP_STATE_SAVE",
          zoom: map.getZoom(),
          bounds: map.getBounds(),
        });

        $tableBody.append(laterRowsCache);
        $progressBar = printerProgressBar.destroy();

        if (
          BatchGeoStore.getState().App.shouldRenderTable &&
          options.hideUnselectedTableRows
        ) {
          tableMap.hideUnselectedRows();
        }

        /**
         * These timeouts might seem strange, but they are here for a
         * reason. They are because the act of printing is a purely side
         * effect, and gmaps can have artifacts if you print too
         * quickly. These artifacts can appear as blank squares in the
         * map
         *
         * Make sure you test this on a very large map if you want to
         * alter the timings.
         */
        setTimeout(function () {
          $(window).trigger("resize");
          setTimeout(function () {
            google.maps.event.trigger(map, "resize");

            // Make sure that what you saw before printing is
            // visible once it resizes to 8.5x11
            map.fitBounds(BatchGeoStore.getState().Printer.bounds);

            // fitBounds will trigger the map to rerender so we
            // need to wait until it's idle again before we call
            // print so that the tiles and everything are loaded
            google.maps.event.addListenerOnce(map, "idle", function () {
              // We need a small buffer because even when it calls
              // idle it seems like the browser is still painting.
              setTimeout(function () {
                options.onComplete.call(this);

                // Hide the print table if table rendering is off
                if (!BatchGeoStore.getState().App.shouldRenderTable) {
                  $printTable.hide();
                }

                if (
                  BatchGeoStore.getState().BatchGeoOptimalRouteToolbar
                    .routeCalculated
                ) {
                  $printTable.hide();
                  $("#page").toggle(true);
                  $("#data").addClass("hide");
                }

                window.print();
              }, 500);
            });
          }, 800);
        }, 800);
      }

      rowWorker.addEventListener("message", workerOnMessage);
      rowWorker.onerror = workerOnError;
      rowWorker.postMessage(chunk);
    }

    function fastEmpty(element) {
      var nodesLength = element.childNodes.length;
      while (nodesLength--) {
        element.removeChild(element.lastChild);
      }
    }

    function deactivate() {
      fastEmpty($printview[0]);
      aboveTheFoldRenderIterationCount = ABOVE_THE_FOLD_ITERATION_AMOUNT;
      rowsRemaining = null;
      laterRowsCache = "";
      $printTable = null;
      $tableBody = null;
      printerProgressBar.destroy();

      // Only if the table rendering is on should we reshow the normal table
      // and make sure all of the rows are displayed
      if (BatchGeoStore.getState().App.shouldRenderTable) {
        tableMap.batchGeoTable.show();
        tableMap.showUnselectedRows();
      } else {
        $("#data").removeClass("hide");
      }

      //you're done, reset the flag.
      cancelPrint = false;
    }

    this.activate = activate;
    this.deactivate = deactivate;
    this.cancelPrinting = cancelPrinting;
    return this;
  }

  window.BatchGeoPrintTable = BatchGeoPrintTable;
})(window);
